/**
 * Banner 5004 — KUK Marketing Banner.
 *
 * Full-bleed CTA band: background photograph, heading, one supporting line and
 * a single button. Ported from kuk-campus-portal's
 * `components/organisms/MarketingBanner.tsx`, whose three hardcoded types
 * (career / EMI / get in touch) become three rows an editor configures here.
 * See `Banner5004.tsx` for the layout-preset and CTA-fill notes.
 *
 * ALSO THE LANDING-PAGE HERO. The hero is the same shape at hero height, so it
 * uses this variant too. Three fields exist only for that role and are inert
 * when left empty: the image scrim, the accreditation cards, and their colours.
 * A marketing band that sets none renders exactly as it did before they existed.
 *
 * Every field maps to a column that already exists on `banner` — the variant
 * adds no schema of its own beyond widening `banner.variant`.
 */

export const BANNER_5004_CONSTANTS = {
  METADATA_TITLES: {
    metadata_1: "Accreditation cards (hero only)",
  },

  DEFAULT_ROW_1: {
    id: "1",
    image: "",
    alt_image: "",
    title: "",
    priority: 1,
    status: true,
  },

  BANNER_FIELDS_CONFIG: [
    {
      fieldName: "backvariant",
      label: "Layout",
      value: "variant_1",
      valueType: "string",
      filedType: "SELECT",
      placeholder: "Select layout",
      description:
        "Also sets where the background image is focused, so the photo's subject stays clear of the copy.",
      required: true,
      defaultValue: "variant_1",
      className: "h-9 py-1.5 text-sm",
      iconClassName: "!size-4",
      options: [
        { label: "Copy left, vertically centred", value: "variant_1" },
        { label: "Copy right, bottom aligned", value: "variant_2" },
      ],
    },
    {
      fieldName: "image",
      label: "Background image",
      value: "",
      valueType: "string",
      filedType: "FILE",
      placeholder: "Upload the band background",
      description:
        "Covers the band. Left empty, the background colour below is painted alone.",
      required: false,
      defaultValue: "",
    },
    {
      fieldName: "mobile_image",
      label: "Background image (mobile)",
      value: "",
      valueType: "string",
      filedType: "FILE",
      placeholder: "Optional — falls back to the main image",
      description: "Used below 768px.",
      required: false,
      defaultValue: "",
    },
    {
      fieldName: "image_alt",
      label: "Background image alt",
      value: "",
      valueType: "string",
      filedType: "INPUT",
      placeholder: "Describe the image",
      required: false,
      defaultValue: "",
    },
    {
      fieldName: "background_color",
      label: "Background colour",
      value: "transparent",
      valueType: "string",
      filedType: "RGBA",
      placeholder: "transparent",
      description:
        "Sits behind the image, so it only shows where the image does not cover — or everywhere, when no image is set.",
      required: false,
      defaultValue: "transparent",
    },
    {
      fieldName: "bleed_background_color",
      label: "Image scrim (hero only)",
      value: "",
      valueType: "string",
      filedType: "RGBA",
      placeholder: "e.g. rgba(0, 0, 0, 0.30)",
      description:
        "Laid OVER the image so white copy stays readable. Leave empty on a marketing band — the reference only scrims the hero.",
      required: false,
      defaultValue: "",
    },
    {
      fieldName: "show_lead_form",
      label: "Inline lead form (hero only)",
      value: false,
      valueType: "boolean",
      filedType: "TOGGLE",
      placeholder: "On = white lead-form card pinned to the hero's right",
      description:
        "Matches the reference hero. Desktop and tablet only — below 768px the card is hidden and the CTA opens the lead-drop popup instead. Needs lead form 5018 configured, or the card renders empty. Inert on a marketing band.",
      required: false,
      defaultValue: false,
    },
    {
      fieldName: "horizontal_stripe_color",
      label: "Accreditation card fill",
      value: "rgba(255, 255, 255, 0.80)",
      valueType: "string",
      filedType: "RGBA",
      placeholder: "rgba(255, 255, 255, 0.80)",
      description: "Only used when accreditation cards are configured below.",
      required: false,
      defaultValue: "rgba(255, 255, 255, 0.80)",
    },
    {
      fieldName: "card_title_color",
      label: "Accreditation card label colour",
      value: "#101112",
      valueType: "string",
      filedType: "COLOR",
      placeholder: "#101112",
      required: false,
      defaultValue: "#101112",
    },
    {
      fieldName: "hero_section_height",
      label: "Band height from 768px up (px) — below that it grows with the copy",
      value: 366,
      valueType: "number",
      filedType: "INPUT",
      placeholder: "366",
      required: false,
      defaultValue: 366,
    },
    {
      fieldName: "title",
      label: "Heading",
      value: "",
      valueType: "string",
      filedType: "TEXTAREA",
      rows: 2,
      placeholder: "e.g. Ready to Take the Next Step?",
      description:
        "Renders as an h2. For a heading that needs more than one colour, use the Rich Text Editor widget instead.",
      required: false,
      defaultValue: "",
    },
    {
      fieldName: "title_color",
      label: "Heading colour",
      value: "#FFFFFF",
      valueType: "string",
      filedType: "COLOR",
      placeholder: "#FFFFFF",
      required: false,
      defaultValue: "#FFFFFF",
    },
    {
      fieldName: "description",
      label: "Supporting line",
      value: "",
      valueType: "string",
      filedType: "TEXTAREA",
      rows: 2,
      placeholder: "e.g. Explore how our programs can elevate your career.",
      required: false,
      defaultValue: "",
    },
    {
      fieldName: "description_color",
      label: "Supporting line colour",
      value: "#FFFFFF",
      valueType: "string",
      filedType: "COLOR",
      placeholder: "#FFFFFF",
      required: false,
      defaultValue: "#FFFFFF",
    },
    {
      fieldName: "apply_now_show",
      label: "Show the CTA button",
      value: true,
      valueType: "boolean",
      filedType: "TOGGLE",
      placeholder: "Show/hide the CTA",
      required: false,
      defaultValue: true,
    },
    {
      fieldName: "apply_now_text",
      label: "CTA text",
      value: "Get in touch",
      valueType: "string",
      filedType: "INPUT",
      placeholder: "Get in touch",
      required: false,
      defaultValue: "Get in touch",
    },
    {
      fieldName: "apply_now_is_cta",
      label: "CTA opens the lead form popup",
      value: true,
      valueType: "boolean",
      filedType: "TOGGLE",
      placeholder: "On = open the popup, off = follow the link below",
      required: false,
      defaultValue: true,
    },
    {
      fieldName: "apply_now_link",
      label: "CTA link",
      value: "",
      valueType: "string",
      filedType: "INPUT",
      placeholder: "https://…",
      description: "Used only when the toggle above is off.",
      required: false,
      defaultValue: "",
    },
    {
      fieldName: "apply_now_color",
      label: "CTA background colour",
      value: "transparent",
      valueType: "string",
      filedType: "RGBA",
      placeholder: "transparent",
      description:
        "Transparent plus a border colour gives the reference's outline button. Ignored when both gradient stops below are set.",
      required: false,
      defaultValue: "transparent",
    },
    {
      fieldName: "apply_now_border_color",
      label: "CTA border colour",
      value: "#FFFFFF",
      valueType: "string",
      filedType: "COLOR",
      placeholder: "#FFFFFF",
      required: false,
      defaultValue: "#FFFFFF",
    },
    {
      fieldName: "cta_text_color",
      label: "CTA text colour",
      value: "#FFFFFF",
      valueType: "string",
      filedType: "COLOR",
      placeholder: "#FFFFFF",
      required: false,
      defaultValue: "#FFFFFF",
    },
    {
      fieldName: "gradient_color_3",
      label: "CTA gradient — start (optional)",
      value: "",
      valueType: "string",
      filedType: "COLOR",
      placeholder: "#CC794E",
      description:
        "Set BOTH gradient stops to fill the button with a gradient; leave either empty to use the flat background colour above.",
      required: false,
      defaultValue: "",
    },
    {
      fieldName: "gradient_color_4",
      label: "CTA gradient — end (optional)",
      value: "",
      valueType: "string",
      filedType: "COLOR",
      placeholder: "#CC3F45",
      required: false,
      defaultValue: "",
    },
    {
      fieldName: "status",
      label: "Status",
      value: true,
      valueType: "boolean",
      filedType: "TOGGLE",
      placeholder: "Enable/Disable section",
      required: false,
      defaultValue: true,
    },
  ],

  METADATA_FIELDS_1: [
    {
      header: "Badge image",
      fieldName: "image",
      value: "",
      valueType: "string",
      filedType: "FILE",
      placeholder: "Upload the accreditation badge",
      defaultValue: "",
    },
    {
      header: "Badge alt",
      fieldName: "alt_image",
      value: "",
      valueType: "string",
      filedType: "INPUT",
      defaultValue: "",
    },
    {
      header: "Label",
      fieldName: "title",
      value: "",
      valueType: "string",
      filedType: "INPUT",
      placeholder: "e.g. UGC Entitled",
      description: "A card with neither a badge nor a label is not rendered.",
      defaultValue: "",
    },
    {
      header: "Priority",
      fieldName: "priority",
      value: 1,
      valueType: "number",
      filedType: "INPUT",
      defaultValue: 1,
    },
    {
      header: "Status",
      fieldName: "status",
      value: true,
      valueType: "boolean",
      filedType: "TOGGLE",
      defaultValue: true,
    },
  ],
};
